import { ReactElement, useState } from 'react';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

import Org from '../../types/Org';
import { VANITY_URL_STATUSES } from '../../types/Org';
import { validatePublicLink } from '../../utils/validators';

interface OrgPublicPageEditorProps {
  org: Org;
  onRequestVanityUrl: (vanityUrl: string) => Promise<void>;
  onCancelVanityUrl?: () => Promise<void>;
}

export default function OrgPublicPageEditor({ org, onRequestVanityUrl, onCancelVanityUrl }: OrgPublicPageEditorProps): ReactElement {
  const [pendingSlug, setPendingSlug] = useState<string | null>(null);
  const [cancelledOptimistically, setCancelledOptimistically] = useState(false);
  const [customLink, setCustomLink] = useState('');
  const [customLinkError, setCustomLinkError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [cancelError, setCancelError] = useState('');

  // Derive display state, with optimistic overrides after submit/cancel
  const isActive = org.vanityUrlStatus === VANITY_URL_STATUSES.ACTIVE;
  const isPending = (org.vanityUrlStatus === VANITY_URL_STATUSES.PENDING || pendingSlug !== null) && !cancelledOptimistically;
  const isRejected = org.vanityUrlStatus === VANITY_URL_STATUSES.REJECTED && pendingSlug === null && !cancelledOptimistically;
  const displayVanityUrl = org.vanityUrl ?? pendingSlug ?? '';

  const validateCustomLink = (value: string): string => {
    if (!value) return '';
    if (!validatePublicLink(value)) {
      return 'Must be 3–50 characters: lowercase letters, numbers, and hyphens (no leading/trailing hyphens)';
    }
    return '';
  };

  const handleCustomLinkChange = (value: string) => {
    const sanitized = value.toLowerCase().replace(/ /g, '-').replace(/[^a-z0-9-]/g, '');
    setCustomLink(sanitized);
    setCustomLinkError(validateCustomLink(sanitized));
    setSubmitError('');
  };

  const handleCancel = async () => {
    if (!onCancelVanityUrl) return;
    setIsCancelling(true);
    setCancelledOptimistically(true);
    setPendingSlug(null);
    setCancelError('');
    try {
      await onCancelVanityUrl();
      setIsCancelConfirmOpen(false);
    } catch (err) {
      setCancelledOptimistically(false);
      setCancelError(err instanceof Error ? err.message : 'Failed to cancel request');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleSubmit = async () => {
    const error = validateCustomLink(customLink);
    if (error) {
      setCustomLinkError(error);
      return;
    }
    setIsSubmitting(true);
    setSubmitError('');
    try {
      await onRequestVanityUrl(customLink);
      setPendingSlug(customLink);
      setCustomLink('');
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit request');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Stack spacing={1}>
      <Divider />
      <Typography variant="labelLarge" component="h3">
        Public page
      </Typography>
      {isActive && (
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} sx={{
            alignItems: "center"
          }}>
            <CheckCircleIcon fontSize="small" color="success" />
            <Typography variant="bodyMedium">Your public page is live</Typography>
          </Stack>
          <Stack spacing={0.5}>
            <Link href={`/public/${org.itemId}`} target="_blank" rel="noopener noreferrer" variant="bodyMedium">
              /public/{org.itemId}
            </Link>
            {displayVanityUrl && (
              <Link href={`/pages/${displayVanityUrl}`} target="_blank" rel="noopener noreferrer" variant="bodyMedium">
                /pages/{displayVanityUrl}
              </Link>
            )}
          </Stack>
        </Stack>
      )}
      {!isActive && (
        <Typography
          variant="bodyMedium"
          sx={{
            color: "text.secondary",
            mb: 2
          }}>
          Request a Gratigolf page for your org. 
          Once approved, your page will be publicly available at https://gratigolf.com/(link)
        </Typography>
      )}
      {isPending && (
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} sx={{
            alignItems: "center"
          }}>
            <HourglassEmptyIcon fontSize="small" color="warning" />
            <Typography variant="bodyMedium">
              Request pending for <strong>/pages/{displayVanityUrl}</strong>
            </Typography>
            <Chip label="Pending" size="small" color="warning" />
            {onCancelVanityUrl && !isCancelConfirmOpen && (
              <Tooltip title="Cancel request">
                <span>
                  <IconButton size="small" color="error" disabled={isCancelling} onClick={() => setIsCancelConfirmOpen(true)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Stack>
          {onCancelVanityUrl && isCancelConfirmOpen && (
            <Stack direction="row" spacing={1} sx={{
              alignItems: "center"
            }}>
              <Typography variant="bodySmall">Cancel this request?</Typography>
              <Button size="small" color="error" variant="contained" disabled={isCancelling} onClick={handleCancel}>
                Confirm
              </Button>
              <Button size="small" disabled={isCancelling} onClick={() => setIsCancelConfirmOpen(false)}>
                Cancel
              </Button>
            </Stack>
          )}
        </Stack>
      )}
      {cancelError && (
        <Alert severity="error">
          {cancelError}
        </Alert>
      )}
      {isRejected && (
        <Alert severity="warning">
          Your request for <strong>/pages/{displayVanityUrl}</strong> was rejected.
          {org.vanityUrlRejectionReason && (
            <> Reason: {org.vanityUrlRejectionReason}</>
          )}
        </Alert>
      )}
      {!isPending && !isActive && (
        <Stack direction="row" spacing={1} sx={{
          alignItems: "flex-start"
        }}>
          <TextField
            size="small"
            label="Org public link"
            placeholder="your-club-name"
            value={customLink}
            onChange={({ target }) => handleCustomLinkChange(target.value)}
            error={!!customLinkError}
            helperText={customLinkError || 'e.g. bay-meadows-golf'}
            slotProps={{ htmlInput: { 'data-1p-ignore': true } }}
            sx={{ maxWidth: '18.75rem' }}
          />
          <Button
            variant="outlined"
            size="small"
            disabled={!customLink || !!customLinkError || isSubmitting}
            onClick={handleSubmit}
            sx={{ mt: 0.5 }}
          >
            {isRejected ? 'Request again' : 'Request'}
          </Button>
        </Stack>
      )}
      {submitError && (
        <Alert severity="error">
          {submitError}
        </Alert>
      )}
    </Stack>
  );
}
